import { PageWidget } from 'app/src/shared/components'
import { useWebAppContext } from 'app/src/shared/hooks'
import { Card } from 'primereact/card'
import { useEffect, type ReactElement } from 'react'

export function Accessibility(): ReactElement {
  const context = useWebAppContext()

  useEffect(() => {
    context.update({ breadCrumbItems: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pdfLinks = [
    {
      label: 'Dichiarazione di Accessibilità mw.postewelfareservizi.it',
      url: '/static/documents/accessibilita/Portale_Welfare_Dichiarazione_di_Accessibilità_2025.pdf',
    },
  ]

  return (
    <>
      <PageWidget containerClassNamesOverrides='mb-1'>
        <h1 className='mb-4 mt-8 text-3xl font-bold'>Dichiarazione di Accessibilità</h1>
        <p className='mb-4'>
          Poste Welfare Servizi, nell&#39;ambito delle diverse iniziative in atto per l&#39;accessibilità e l&#39;inclusione, è
          costantemente impegnata nel rendere i propri siti web e applicazioni fruibili da tutti.
        </p>

        <ul className='my-4 mb-8 pl-1'>
          {pdfLinks.map((pdf, idx) => (
            <li key={idx}>
              <i className='pi pi-paperclip mr-2 text-xl text-[#0047bb]'></i>
              <a
                href={pdf.url}
                className='text-[16px] font-semibold leading-[24px] text-[#0047bb]'
                target='_blank'
                rel='noopener noreferrer'
              >
                {pdf.label} (PDF)
              </a>
            </li>
          ))}
        </ul>

        <Card title='Informazioni sulle caratteristiche di accessibilità e compatibilità' className='mb-4'>
          <p className='mb-2'>
            I siti e le app di Poste Welfare Servizi sono stati concepiti per essere compatibili con le seguenti tecnologie
            assistive più utilizzate:
          </p>
          <ul className='mb-2'>
            <li>
              <i className='pi pi-check-circle text-[#0047bb]' /> Ultime versioni dei browser Google Chrome, Microsoft Edge,
              Mozilla Firefox e Apple Safari;
            </li>
            <li>
              {' '}
              <i className='pi pi-check-circle text-[#0047bb]' /> Software di screen reader come NVDA, JAWS, VoiceOver e Talkback;
            </li>
          </ul>
          <p className='mb-2'>
            L&#39;accessibilità dei siti e delle app si basano sulle seguenti specifiche tecniche che permettono il funzionamento
            con la particolare combinazione di browser ed eventuali tecnologie assistive o plugin installati sul tuo computer o
            smartphone:
          </p>
          <ul>
            <li>
              <i className='pi pi-check-circle text-[#0047bb]' /> HTML;
            </li>
            <li>
              <i className='pi pi-check-circle text-[#0047bb]' /> WAI-ARIA;
            </li>
            <li>
              <i className='pi pi-check-circle text-[#0047bb]' /> CSS;
            </li>
            <li>
              <i className='pi pi-check-circle text-[#0047bb]' /> JavaScript;
            </li>
          </ul>
        </Card>

        <Card title='Cosa si intende per accessibilità' className='mb-4'>
          <p>
            Come indicato nella Legge n. 4 del 9 gennaio 2004 e ss.mm., per Accessibilità si intende “La capacità dei sistemi
            informatici di erogare servizi e fornire informazioni fruibili, senza discriminazioni, anche da parte di coloro che a
            causa di disabilità necessitano di tecnologie assistive o configurazioni particolari”.
          </p>
        </Card>

        <Card title='Come effettuare una segnalazione' className='mb-4'>
          <p className='mb-3'>
            Chiunque ne abbia interesse può segnalare eventuali non conformità dei siti web e delle applicazioni mobili di Poste
            Welfare Servizi ai principi di accessibilità.
          </p>
          <p className='mb-3'>
            Per rendere efficaci le segnalazioni, è necessario indicare le situazioni di inaccessibilità descrivendo il problema e
            identificando, con quanta più precisione possibile, la parte del sito web o dell&#39;applicazione mobile interessata.
          </p>
          <p className='mb-3'>
            La segnalazione dovrà essere inviata al seguente indirizzo di posta elettronica dedicato:
            <br />
            <a href='mailto:accessibilita@posteitaliane.it' className='text-blue-600 underline'>
              accessibilita@posteitaliane.it
            </a>
          </p>
          <p className='mb-2'>Nella segnalazione dovranno essere riportate le seguenti informazioni:</p>
          <ul className='list-none pl-0'>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>nome e cognome</b> del segnalante;
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>url della pagina web</b> o la funzionalità dell&#39;app oggetto della segnalazione;
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>descrizione</b> chiara e sintetica del problema riscontrato;
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>strumenti</b> utilizzati per la navigazione;
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>dispositivo</b> usato (Smartphone, PC Fisso, Tablet, PC Portatile-Laptop);
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>sistema Operativo</b> usato (Windows, Linux, Mac OS);
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>browser</b> usato (Internet Explorer, Chrome, Edge, Firefox, Safari, Opera);
              </span>
            </li>
            <li className='mb-1 flex items-start gap-2'>
              <i className='pi pi-check-circle text-[#0047bb]' />
              <span>
                <b>tecnologie assistive</b> adoperate (Jaws, Chrome Vox, ToVoice, Nvda, Google Talk, ecc.), specificando se
                possibile anche la versione.
              </span>
            </li>
          </ul>
          <p className='mt-4'>
            Eventuali richieste non attinenti a problemi di Accessibilità (ad esempio problemi di natura tecnica come reset
            password) dovranno essere inviate al seguente indirizzo di posta elettronica dedicato:{' '}
            <a href='mailto:backofficeflex@postewelfareservizi.it' className='text-blue-600 underline'>
              backofficeflex@postewelfareservizi.it
            </a>
            .
          </p>
        </Card>
      </PageWidget>
    </>
  )
}
